<script setup>
/**
 * Dynamic CRM Analytics
 * - Uses your Pinia stores:
 *    - useDealStore()       -> deals with stage/owner/amount
 *    - useContactStore()    -> contacts
 *    - useCrmClientStore()  -> clients/companies
 *    - useCrmActivityStore()-> recent activities
 *
 * Charts:
 *  - Deals by Stage (stacked bar, % per stage, remaining-to-100 background)
 *  - Leads by Source (donut; computed from leads' .source)
 *
 * Tables:
 *  - Recently Created Contacts (date filtered)
 *  - Recent Deals (last 5 by created_at)
 *  - Recent Leads (last 5 by created_at)
 *  - Recently Created Clients (date filtered)
 *  - Recent Activities (timeline from activities store)
 */

import { ref, reactive, computed, onMounted, watch } from "vue";
import { useDealStore } from "@/stores/dealStore";
import { useContactStore } from "@/stores/contactStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useCrmActivityStore } from "@/stores/crmActivityStore";
import * as Toast from "@/utils/toast";
import { useLeadStore } from "@/stores/leadStore";

/* -------------------- Page header meta -------------------- */
const title = "Analytics";
const text = "CRM";
const text1 = "Analytics";

/* -------------------- Stores -------------------- */
const dealStore = useDealStore();
const contactStore = useContactStore();
const clientStore = useCrmClientStore();
const activityStore = useCrmActivityStore();
const leadStore = useLeadStore();

/* -------------------- Date range & period -------------------- */
/** Period selector for quick filters */
// const period = ref<"this_week" | "this_month" | "last_week">("this_week");

const period = ref("this_week");

const periodLabel = computed(() => String(period.value).replace("_", " "));

/** Single date displayed in the header date-picker (cosmetic); we drive real range from `period` */
const startdate = ref(new Date());
const dateFormat = "dd-MM-yyyy";

/** Compute [from, to) UTC-less YYYY-MM-DD strings for store filters */
function startOfWeek(d = new Date()) {
  const x = new Date(d);
  const day = x.getDay(); // 0..6 (Sun..Sat)
  const diff = (day === 0 ? -6 : 1) - day; // Monday as start
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfWeek(d = new Date()) {
  const s = startOfWeek(d);
  const e = new Date(s);
  e.setDate(s.getDate() + 7);
  return e;
}
function startOfMonth(d = new Date()) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfMonth(d = new Date()) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  x.setHours(0, 0, 0, 0);
  return x;
}
const range = computed(() => {
  const now = new Date();
  if (period.value === "this_month") {
    return { from: startOfMonth(now), to: endOfMonth(now) };
  }
  if (period.value === "last_week") {
    const lastWeekEnd = startOfWeek(now); // this Monday
    const lastWeekStart = new Date(lastWeekEnd);
    lastWeekStart.setDate(lastWeekEnd.getDate() - 7);
    return { from: lastWeekStart, to: lastWeekEnd };
  }
  // this_week
  return { from: startOfWeek(now), to: endOfWeek(now) };
});
function fmt(d) {
  // YYYY-MM-DD
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/* -------------------- Data loading -------------------- */
async function loadAll() {
  const { from, to } = range.value;
  const params = {
    date_from: fmt(from),
    date_to: fmt(to),
    per_page: 100, // pull a sensible page for analytics
    page: 1,
  };

  try {
    // Deals
    await dealStore.fetchAll(params);

    // Contacts
    await contactStore.fetchAll(params);

    // Leads
    await leadStore.fetchAll(params);

    // Clients/Companies
    await clientStore.fetchClients(params);

    // Activities (recent timeline; we’ll ignore date filter if the service doesn't support it)
    await activityStore.load?.(params);
  } catch (e) {
    const msg =
      e?.response?.data?.message || e.message || "Failed to load analytics";
    if (Toast?.toast?.error) Toast.toast.error(msg);
    else console.error(msg);
  }
}

onMounted(loadAll);
watch(period, loadAll);

/* -------------------- Deals by Stage (stacked percent bar) -------------------- */
const stageOrder = ["Inpipeline", "Follow Up", "Schedule", "Conversion"]; // keep UI labels
function readStageName(d) {
  return (
    d?.stage?.name ||
    d?.stage?.title ||
    d?.stage?.label ||
    d?.stage ||
    d?.status ||
    "-"
  );
}
const stageCounts = computed(() => {
  const counts = Object.fromEntries(stageOrder.map((s) => [s, 0]));
  (dealStore.items?.value || dealStore.items || []).forEach((d) => {
    const n = readStageName(d);
    if (counts[n] == null && typeof n === "string") counts[n] = 0; // seen a new stage, keep it
    if (counts[n] != null) counts[n] += 1;
  });
  return counts;
});
const stagePercents = computed(() => {
  const total =
    Object.values(stageCounts.value).reduce((a, b) => a + b, 0) || 1;
  return stageOrder.map((s) =>
    Math.round((100 * (stageCounts.value[s] || 0)) / total)
  );
});
const dealsStageOptions = computed(() => ({
  chart: { height: 310, type: "bar", stacked: true, toolbar: { show: false } },
  colors: ["#FF6F28", "#F8F9FA"],
  plotOptions: {
    bar: { borderRadius: 5, horizontal: false, endingShape: "rounded" },
  },
  xaxis: {
    categories: stageOrder,
    labels: { style: { colors: "#6B7280", fontSize: "13px" } },
  },
  yaxis: {
    labels: { offsetX: -15, style: { colors: "#6B7280", fontSize: "13px" } },
    max: 100,
  },
  grid: { borderColor: "#E5E7EB", strokeDashArray: 5 },
  legend: { show: false },
  dataLabels: { enabled: false },
  fill: { opacity: 1 },
}));
const dealsStageSeries = computed(() => {
  const pct = stagePercents.value;
  const remain = pct.map((v) => 100 - v);
  return [
    { name: "Share", data: pct },
    { name: "Remaining", data: remain },
  ];
});

/* -------------------- Leads by Source (donut from leads.source) -------------------- */
const sourcePalette = [
  "#FFC107",
  "#0C4B5E",
  "#AB47BC",
  "#FD3995",
  "#22C55E",
  "#F97316",
];
function readSource(c) {
  return c?.source || "Unknown";
}
const leadsBySourceData = computed(() => {
  const map = new Map();
  (leadStore.items || []).forEach((lead) => {
    const key = readSource(lead);
    map.set(key, (map.get(key) || 0) + 1);
  });
  // sort desc, take top 6
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
});
const leadsTotal = computed(
  () => leadsBySourceData.value.reduce((a, [, v]) => a + v, 0) || 1
);
const leadsSeries = computed(() => leadsBySourceData.value.map(([, v]) => v));
const leadsLabels = computed(() => leadsBySourceData.value.map(([k]) => k));
const topSource = computed(() => {
  const [name, value] = leadsBySourceData.value[0] || ["—", 0];
  return { name, pct: Math.round((100 * value) / (leadsTotal.value || 1)) };
});
const donutOptions = computed(() => ({
  chart: { type: "donut", height: 185 },
  labels: leadsLabels.value,
  colors: sourcePalette.slice(0, leadsLabels.value.length),
  plotOptions: {
    pie: {
      donut: {
        size: "60%",
        labels: {
          show: true,
          total: {
            show: true,
            label: topSource.value.name,
            formatter: () => `${topSource.value.pct}%`,
          },
        },
      },
    },
  },
  stroke: { lineCap: "round", show: true, width: 0, colors: "#fff" },
  legend: { show: false },
  dataLabels: { enabled: false },
}));

/* -------------------- “This Week / Month / Last Week” menus -------------------- */
function setPeriod(v) {
  period.value = v;
}

/* -------------------- Tables (recent lists) -------------------- */
function byDateDesc(a, b, key = "created_at") {
  const ta = new Date(a?.[key] || a?.createdAt || a?.created_on || 0).getTime();
  const tb = new Date(b?.[key] || b?.createdAt || b?.created_on || 0).getTime();
  return tb - ta;
}
const recentContacts = computed(() => {
  const arr = (contactStore.items || [])
    .slice()
    .sort((a, b) => byDateDesc(a, b));
  return arr.slice(0, 5);
});

const recentDeals = computed(() => {
  const arr = (dealStore.items?.value || dealStore.items || [])
    .slice()
    .sort((a, b) => byDateDesc(a, b));
  return arr.slice(0, 5);
});

const recentLeads = computed(() => {
  const arr = (leadStore.items || []).slice().sort((a, b) => byDateDesc(a, b));
  return arr.slice(0, 5);
});

const recentClients = computed(() => {
  const arr = (clientStore.clients?.value || clientStore.clients || [])
    .slice()
    .sort((a, b) => byDateDesc(a, b));
  return arr.slice(0, 5);
});

/* -------------------- Activities timeline -------------------- */
const recentActivities = computed(() => {
  const arr = (activityStore.items || [])
    .slice()
    .sort((a, b) => byDateDesc(a, b));
  return arr.slice(0, 4);
});
function activityIcon(type) {
  switch ((type || "").toLowerCase()) {
    case "call":
      return { cls: "bg-success", icon: "ti ti-phone" };
    case "email":
      return { cls: "bg-info", icon: "ti ti-message-circle-2" };
    case "meeting":
      return { cls: "bg-purple", icon: "ti ti-user-circle" };
    default:
      return { cls: "bg-secondary", icon: "ti ti-activity" };
  }
}

function ownerLabel(owner, ownerId = null) {
  const fullName = [owner?.first_name, owner?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (fullName) return fullName;
  if (owner?.name) return owner.name;
  if (owner?.email) return owner.email;
  return `User #${ownerId || "-"}`;
}

function displayDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString().slice(0, 10);
  }
  return String(value).split("T")[0] || "-";
}

/* -------------------- Export (stub) -------------------- */
function downloadCsv(filename, header, rows) {
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportPdf() {
  const rows = [
    ...recentContacts.value.map((contact) => ["Contact", contact.name || "", contact.email || "", contact.phone || ""]),
    ...recentDeals.value.map((deal) => ["Deal", deal.name || "", deal.stage?.name || "", deal.amount || 0]),
    ...recentLeads.value.map((lead) => ["Lead", lead.name || "", lead.source || "", lead.amount || 0]),
    ...recentClients.value.map((client) => ["Account", client.name || "", client.email || "", client.status || ""]),
  ];
  downloadCsv(
    `crm-analytics-${period.value}-${new Date().toISOString().slice(0, 10)}.csv`,
    ["Section", "Name", "Metric 1", "Metric 2"],
    rows
  );
}
function exportExcel() {
  const rows = recentActivities.value.map((activity) => [
    activity.title || "",
    activity.type || "",
    [activity.owner?.first_name, activity.owner?.last_name].filter(Boolean).join(" ") || "",
    activity.created_at || "",
  ]);
  downloadCsv(
    `crm-analytics-activities-${period.value}-${new Date().toISOString().slice(0, 10)}.csv`,
    ["Activity", "Type", "Owner", "Created At"],
    rows
  );
}
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap"
        >
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" @click.prevent="exportPdf"
                    v-can="'Analytics.Export'"
                    ><i class="ti ti-file-type-csv me-1"></i>Export Summary CSV</a
                  >
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportExcel"
                    v-can="'Analytics.Export'"
                    ><i class="ti ti-file-type-csv me-1"></i>Export Activity CSV</a
                  >
                </li>
              </ul>
            </div>
          </div>
          <div class="input-icon w-120 position-relative mb-2">
            <span class="input-icon-addon"
              ><i class="ti ti-calendar text-gray-9"></i
            ></span>
            <!-- Cosmetic date display; period controls actually change data -->
            <date-picker
              v-model="startdate"
              class="form-control datetimepicker"
              :editable="true"
              :clearable="false"
              :input-format="dateFormat"
            />
          </div>
          <div class="ms-2">
            <div class="btn-group">
              <button
                class="btn btn-light btn-sm"
                :class="{ active: period === 'this_month' }"
                @click="setPeriod('this_month')"
              >
                This Month
              </button>
              <button
                class="btn btn-light btn-sm"
                :class="{ active: period === 'this_week' }"
                @click="setPeriod('this_week')"
              >
                This Week
              </button>
              <button
                class="btn btn-light btn-sm"
                :class="{ active: period === 'last_week' }"
                @click="setPeriod('last_week')"
              >
                Last Week
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row">
        <!-- LEFT COLUMN -->
        <div class="col-xl-6">
          <!-- Recently Created Contacts -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h5>Recently Created Contacts</h5>
              <span class="text-muted small"
                >Showing last {{ recentContacts.length }}</span
              >
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Created at</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in recentContacts" :key="c.id">
                      <td>
                        <div class="d-flex align-items-center file-name-icon">
                          <span
                            class="avatar avatar-md border avatar-rounded d-inline-flex align-items-center justify-content-center"
                          >
                            <i class="ti ti-user"></i>
                          </span>
                          <div class="ms-2">
                            <h6 class="fw-medium mb-0">
                              {{
                                c.name ||
                                c.first_name + " " + (c.last_name || "") ||
                                "Contact #" + c.id
                              }}
                            </h6>
                            <span class="fs-12 fw-normal">{{
                              c.job_title || "-"
                            }}</span>
                          </div>
                        </div>
                      </td>
                      <td>{{ c.email || "-" }}</td>
                      <td>{{ c.phone || c.phone_alt || "-" }}</td>
                      <td>{{ (c.created_at || "").split("T")[0] || "-" }}</td>
                    </tr>
                    <tr v-if="!recentContacts.length">
                      <td colspan="4" class="text-center text-muted">
                        No contacts in this period
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Won Deals Stage (summary circles kept static UI, but values are dynamic) -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h6>Won Deals Stage</h6>
              <div class="text-muted small">Period: {{ periodLabel }}</div>
            </div>
            <div class="card-body">
              <div class="text-center mb-4">
                <p class="mb-1 fw-medium">
                  Stages Share (of all deals this period)
                </p>
                <div class="d-flex align-items-center justify-content-center">
                  <h3 class="me-2">
                    {{ stagePercents.reduce((a, b) => a + b, 0) }}%
                  </h3>
                  <span
                    class="badge badge-soft-danger border-danger border rounded-pill me-1"
                    >{{ stageOrder[0] }} top: {{ stagePercents[0] || 0 }}%</span
                  >
                </div>
              </div>
              <div class="stage-chart-main">
                <div class="deal-stage-chart">
                  <div
                    class="text-center d-flex align-items-center justify-content-center flex-column bg-secondary rounded-circle chart-stage-1"
                  >
                    <span class="d-block text-white mb-1">Conversion</span>
                    <h6 class="text-white">{{ stagePercents[3] || 0 }}%</h6>
                  </div>
                  <div
                    class="text-center d-flex align-items-center justify-content-center flex-column bg-danger rounded-circle chart-stage-2"
                  >
                    <span class="d-block text-white mb-1">Calls</span>
                    <h6 class="text-white">{{ stagePercents[1] || 0 }}%</h6>
                  </div>
                  <div
                    class="text-center d-flex align-items-center justify-content-center flex-column bg-warning rounded-circle chart-stage-3"
                  >
                    <span class="d-block text-white mb-1">Email</span>
                    <h6 class="text-white">{{ stagePercents[0] || 0 }}%</h6>
                  </div>
                  <div
                    class="text-center d-flex align-items-center justify-content-center flex-column bg-success rounded-circle chart-stage-4"
                  >
                    <span class="d-block text-white mb-1">Chats</span>
                    <h6 class="text-white">{{ stagePercents[2] || 0 }}%</h6>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Deals -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h5>Recent Deals</h5>
              <router-link
                to="/crm/deals-list"
                class="btn btn-sm btn-light px-3"
                v-can="'Deal.Read'"
                >View All</router-link
              >
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Deal Name</th>
                      <th>Stage</th>
                      <th>Deal Value</th>
                      <th>Owner</th>
                      <th>Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in recentDeals" :key="d.id">
                     
                      <td>
                        <h6 class="mb-0">{{ d.name || "Deal #" + d.id }}</h6>
                      </td>
                      <td>{{ readStageName(d) }}</td>
                      <td>
                        {{ d.currency || "" }}
                        {{ (d.amount ?? 0).toLocaleString() }}
                      </td>
                      <td>
                        {{ d.owner?.first_name }} {{ " " }}
                        {{ d.owner.last_name }}
                      </td>
                      <td>{{ displayDate(d.created_at || d.createdAt || d.created_on) }}</td>
                    </tr>
                    <tr v-if="!recentDeals.length">
                      <td colspan="5" class="text-center text-muted">
                        No deals in this period
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Recent Leads  -->
          <div class="card flex-fill">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h5>Recent Leads</h5>
              <router-link
                to="/crm/leads-list"
                class="btn btn-sm btn-light px-3"
                v-can="'Lead.Read'"
                >View All</router-link
              >
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Lead Name</th>
                      <th>Company Name</th>
                      <th>Source</th>
                      <th>Created Date</th>
                      <th>Lead Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="l in recentLeads" :key="l.id">
                      <!-- <td>{{ l }}</td> -->
                      <td>
                        <h6 class="mb-0">{{ l.name || "Lead #" + l.id }}</h6>
                      </td>
                      <td>{{ l.company_name || "-" }}</td>
                      <td>
                        <span
                          class="badge badge-secondary d-inline-flex align-items-center"
                        >
                          <i class="ti ti-point-filled me-1"></i
                          >{{ l.source || "Unknown" }}</span
                        >
                      </td>
                      <td>{{ displayDate(l.created_at || l.createdAt || l.created_on) }}</td>
                      <td>{{ ownerLabel(l.owner, l.owner_id) }}</td>
                    </tr>
                    <tr v-if="!recentLeads.length">
                      <td colspan="5" class="text-center text-muted">
                        No leads in this period
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="col-xl-6">
          <!-- Deals by Stage (chart) -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h6>Deals by Stage</h6>
              <div class="dropdown">
                <a
                  class="btn btn-white border btn-sm d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i class="ti ti-calendar me-1"></i>{{ periodLabel }}
                </a>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('this_month')"
                      >This Month</a
                    >
                  </li>
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('this_week')"
                      >This Week</a
                    >
                  </li>
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('last_week')"
                      >Last Week</a
                    >
                  </li>
                </ul>
              </div>
            </div>
            <div class="card-body pb-0">
              <div class="d-flex align-items-center mb-2">
                <h3 class="me-2">{{ Math.max(...stagePercents) || 0 }}%</h3>
                <span
                  class="badge badge-outline-success bg-success-transparent rounded-pill me-1"
                >
                  {{
                    stageOrder[
                      stagePercents.indexOf(Math.max(...stagePercents))
                    ] || "-"
                  }}
                </span>
                <span>top stage share</span>
              </div>
              <div id="deals_stage">
                <apexchart
                  type="bar"
                  height="310"
                  :options="dealsStageOptions"
                  :series="dealsStageSeries"
                />
              </div>
            </div>
          </div>

          <!-- Recent Activities -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h6>Recent Activities</h6>
              <router-link to="/crm/activity" class="btn btn-sm btn-light px-3"
                v-can="'Activity.Read'"
                >View All</router-link
              >
            </div>
            <div class="card-body schedule-timeline activity-timeline">
              <div
                v-for="a in recentActivities"
                :key="a.id"
                class="d-flex align-items-start"
              >
                <div
                  class="avatar avatar-md avatar-rounded flex-shrink-0"
                  :class="activityIcon(a.type).cls"
                >
                  <i :class="activityIcon(a.type).icon + ' fs-20'"></i>
                </div>
                <div class="flex-fill ps-3 pb-4 timeline-flow">
                  <p class="fw-medium text-gray-9 mb-1">
                    <router-link to="/crm/activity">{{
                      a.title || (a.type ? a.type.toUpperCase() : "Activity")
                    }}</router-link>
                  </p>
                  <span>{{
                    (a.due_at || a.created_at || "")
                      .replace("T", " ")
                      .slice(0, 16)
                  }}</span>
                </div>
              </div>
              <div
                v-if="!recentActivities.length"
                class="text-muted text-center"
              >
                No activities
              </div>
            </div>
          </div>

          <!-- Leads by Source (donut) -->
          <div class="card flex-fill">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h6>Leads by Source</h6>
              <div class="dropdown">
                <a
                  class="btn btn-white border btn-sm d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i class="ti ti-calendar me-1"></i>{{ periodLabel }}
                </a>
                <ul class="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('this_month')"
                      >This Month</a
                    >
                  </li>
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('this_week')"
                      >This Week</a
                    >
                  </li>
                  <li>
                    <a
                      class="dropdown-item rounded-1"
                      @click.prevent="setPeriod('last_week')"
                      >Last Week</a
                    >
                  </li>
                </ul>
              </div>
            </div>
            <div class="card-body">
              <div id="donut-chart-2">
                <apexchart
                  type="donut"
                  height="185"
                  :options="donutOptions"
                  :series="leadsSeries"
                />
              </div>
              <div class="mt-3">
                <h6 class="mb-3">Status</h6>
                <div
                  v-for="(label, i) in leadsLabels"
                  :key="'src-' + i"
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <p class="f-13 mb-0">
                    <i
                      class="ti ti-circle-filled me-1"
                      :style="{
                        color: sourcePalette[i % sourcePalette.length],
                      }"
                    ></i
                    >{{ label }}
                  </p>
                  <p class="f-13 fw-medium text-gray-9">
                    {{
                      Math.round((100 * leadsSeries[i]) / (leadsTotal || 1))
                    }}%
                  </p>
                </div>
                <div v-if="!leadsLabels.length" class="text-muted">
                  No leads in this period
                </div>
              </div>
            </div>
          </div>

          <!-- Recently Created Clients -->
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2"
            >
              <h5>Recently Created Clients</h5>
              <span class="text-muted small"
                >Showing last {{ recentClients.length }}</span
              >
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Company Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Created at</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="co in recentClients" :key="co.id">
                      <td>
                        <div class="d-flex align-items-center file-name-icon">
                          <span
                            class="avatar avatar-md border rounded-circle d-inline-flex align-items-center justify-content-center"
                          >
                            <i class="ti ti-building"></i>
                          </span>
                          <div class="ms-2">
                            <h6 class="fw-medium mb-0">
                              {{
                                co.name || co.company_name || "Client #" + co.id
                              }}
                            </h6>
                          </div>
                        </div>
                      </td>
                      <td>{{ co.email || "-" }}</td>
                      <td>{{ co.phone || "-" }}</td>
                      <td>{{ (co.created_at || "").split("T")[0] || "-" }}</td>
                    </tr>
                    <tr v-if="!recentClients.length">
                      <td colspan="4" class="text-center text-muted">
                        No clients in this period
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Preserve your look & feel; minor guardrails for charts/scroll */
.stage-chart-main .deal-stage-chart {
  position: relative;
  min-height: 160px;
}
.chart-stage-1,
.chart-stage-2,
.chart-stage-3,
.chart-stage-4 {
  width: 120px;
  height: 120px;
  margin: 10px;
  display: inline-flex;
}
</style>


